import { BellRing, BellOff, HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ConsentStatus } from '../../services/customerService';

/**
 * Whether this customer may be advertised to over WhatsApp.
 *
 * Three states, not two: "nie gefragt" is not the same as "abgelehnt", and
 * collapsing them would hide how much of the list is still reachable. Silence
 * is never consent, so both non-granted states read as "no" — the difference
 * only says whether asking again makes sense.
 */
const CONFIG: Record<ConsentStatus, { label: string; title: string; icon: typeof BellRing; className: string }> = {
  granted: {
    label: 'Werbung erlaubt',
    title: 'Hat der Werbung über WhatsApp ausdrücklich zugestimmt',
    icon: BellRing,
    className: 'border-success/30 bg-success/10 text-success-strong',
  },
  revoked: {
    label: 'Keine Werbung',
    title: 'Hat abgelehnt oder widerrufen – darf nicht beworben werden',
    icon: BellOff,
    className: 'border-border bg-muted/60 text-muted-foreground',
  },
  never_asked: {
    label: 'Nicht gefragt',
    title: 'Wurde noch nicht um Einwilligung gebeten – darf nicht beworben werden',
    icon: HelpCircle,
    className: 'border-border/60 bg-transparent text-muted-foreground',
  },
};

export default function ConsentBadge({
  status,
  className,
}: {
  status: ConsentStatus;
  className?: string;
}) {
  const config = CONFIG[status] ?? CONFIG.never_asked;
  const Icon = config.icon;
  return (
    <span
      title={config.title}
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
        config.className,
        className,
      )}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {config.label}
    </span>
  );
}
